import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import { PHONE, PHONE_LINK } from "@/data/content";

const HERO_IMG =
    "https://images.unsplash.com/photo-1624555130581-1d9cca783bc0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjBsYXd5ZXIlMjBjb25zdWx0YW50JTIwbWVldGluZyUyMG9mZmljZXxlbnwwfHx8fDE3OTA0ODY1NjF8MA&ixlib=rb-4.1.0&q=85";

const MaskedLine = ({ children, delay }) => (
    <span className="hero-mask-line">
        <motion.span
            className="block"
            initial={{ y: "112%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.95, delay, ease: [0.22, 1, 0.36, 1] }}
        >
            {children}
        </motion.span>
    </span>
);

const STATS = [
    { value: "10+", label: "Compliance Domains" },
    { value: "13", label: "Industries Served" },
    { value: "8-Step", label: "Due-Date Workflow" },
    { value: "100%", label: "Deadline Focus" },
];

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const bgY = useTransform(scrollYProgress, [0, 1], [0, 160]);
    const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0.15]);

    return (
        <section ref={ref} id="top" className="relative min-h-screen flex flex-col justify-end overflow-hidden grain">
            <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
                <img src={HERO_IMG} alt="Compliance professionals reviewing statutory documentation" className="w-full h-[115%] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-b from-[#0A1128]/60 via-[#0A1128]/72 to-[#0A1128]" />
                <div className="absolute inset-0 bg-gradient-to-r from-[#0A1128]/85 via-[#0A1128]/30 to-[#0A1128]/40" />
            </motion.div>
            <div className="absolute inset-0 z-0 ruled" />

            <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-40 pb-10">
                <motion.div
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.15 }}
                    className="inline-flex items-center gap-2.5 border border-sky-400/30 bg-sky-400/5 rounded-full px-4 py-2 mb-8"
                    data-testid="hero-badge"
                >
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 led" />
                    <span className="font-mono2 text-[10px] sm:text-[11px] tracking-[0.22em] uppercase text-sky-300">
                        Gujarat's Statutory Compliance Partner — Ahmedabad
                    </span>
                </motion.div>

                <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-[4.4rem] leading-[1.05] tracking-tight text-white max-w-5xl" data-testid="hero-headline">
                    <MaskedLine delay={0.25}>Complete Labour Law &</MaskedLine>
                    <MaskedLine delay={0.38}>
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-cyan-300">Statutory Compliance</span>
                    </MaskedLine>
                    <MaskedLine delay={0.51}>Solutions for Your Business</MaskedLine>
                </h1>

                <motion.p
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.75 }}
                    className="mt-6 max-w-2xl text-base sm:text-lg text-slate-300 leading-relaxed"
                    data-testid="hero-subheading"
                >
                    Pramukh Compliance Services helps businesses manage registrations, labour-law compliance,
                    statutory registers, due dates and government documentation — with accuracy and timely support.
                </motion.p>

                <motion.div
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.9 }}
                    className="mt-9 flex flex-wrap items-center gap-4"
                >
                    <a
                        href="#contact"
                        data-testid="hero-cta-consultation"
                        className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm transition-all duration-300 hover:gap-3"
                    >
                        Request a Consultation
                        <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5" />
                    </a>
                    <a
                        href="#services"
                        data-testid="hero-cta-services"
                        className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/25 hover:border-sky-400/60 hover:text-sky-300 text-white font-semibold text-sm transition-colors duration-300"
                    >
                        Our Services
                    </a>
                    <a
                        href={PHONE_LINK}
                        data-testid="hero-cta-call"
                        className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors"
                    >
                        <Phone size={15} className="text-sky-400" />
                        <span className="font-mono2 text-xs tracking-wider">{PHONE}</span>
                    </a>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.8, delay: 1.15 }}
                    className="mt-16 grid grid-cols-2 lg:grid-cols-4 border-t border-white/10"
                    data-testid="hero-stats"
                >
                    {STATS.map((s, i) => (
                        <div key={s.label} className={`py-6 pr-6 ${i > 0 ? "lg:border-l lg:border-white/10 lg:pl-8" : ""}`}>
                            <div className="font-display text-3xl sm:text-4xl font-bold text-white">{s.value}</div>
                            <div className="mt-1 font-mono2 text-[10px] tracking-[0.2em] uppercase text-slate-400">{s.label}</div>
                        </div>
                    ))}
                </motion.div>
            </motion.div>
        </section>
    );
};

export default Hero;
